import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getStudentCourse, getModuleDetail, markModuleComplete, submitQuiz } from '../../api/studentApi';
import StudentSidebar from '../../components/student/StudentSidebar';
import { toTitleCase } from '../../utils/format';

async function downloadFile(filePath, fileName) {
  try {
    const token = sessionStorage.getItem('af_token');
    const res = await fetch(`/api/instructor/uploads/${filePath}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) { alert('File not found.'); return; }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName || filePath.split('/').pop();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch { alert('Could not download file.'); }
}


function fmtDuration(sec) {
  if (!sec) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function StudentModule() {
  const { courseId, moduleId } = useParams();
  const [courseData, setCourseData] = useState(null);
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [quizMode, setQuizMode] = useState(null); // null | 'taking' | 'submitted'
  const [answers, setAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [quizResult, setQuizResult] = useState(null);
  const [marking, setMarking] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null); // seconds remaining, null = no limit
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const autoSubmittedRef = useRef(false);
  const submitQuizRef = useRef(null); // stable ref so the timer effect can call it
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      getStudentCourse(courseId),
      getModuleDetail(courseId, moduleId),
    ]).then(([cd, md]) => {
      setCourseData(cd);
      setData(md);
      setProgress(md.progress);
    }).catch(console.error);
  }, [courseId, moduleId]);

  // ── Countdown timer — MUST be before any early return ──
  useEffect(() => {
    if (quizMode !== 'taking' || timeLeft === null) return;
    if (timeLeft <= 0) {
      if (!autoSubmittedRef.current) {
        autoSubmittedRef.current = true;
        submitQuizRef.current?.();
      }
      return;
    }
    const id = setTimeout(() => setTimeLeft(t => t - 1), 1000);
    return () => clearTimeout(id);
  }, [quizMode, timeLeft]);

  if (!data || !courseData) return (
    <div className="app">
      <StudentSidebar course={null} modules={[]} currentModuleId={null} />
      <div className="main"><div className="body" style={{ color: 'var(--muted)', padding: 40 }}>Loading…</div></div>
    </div>
  );

  const { module: mod, quiz, resources } = data;
  const { course, modules: allModules } = courseData;

  const isCompleted = progress?.is_completed;
  const bestScore = progress?.best_score ?? null;
  const hasPassed = bestScore !== null && bestScore >= quiz.passmark;
  const attempts = progress?.attempts ?? [];

  const sidebarCourse = { id: course.id, title: course.title, emoji: course.emoji };
  const sidebarModules = allModules.map(m => ({ id: m.id, title: m.title, is_completed: m.is_completed, status: m.status }));

  function startQuiz() {
    setAnswers({});
    setCurrentQ(0);
    setQuizResult(null);
    autoSubmittedRef.current = false;
    setTimeLeft(quiz.time_limit ? quiz.time_limit * 60 : null);
    setQuizMode('taking');
    window.scrollTo(0, 0);
  }

  async function handleSubmitQuiz() {
    const payload = { answers: {} };
    for (const [qId, optId] of Object.entries(answers)) {
      payload.answers[qId] = optId;
    }
    try {
      const result = await submitQuiz(courseId, moduleId, payload);
      setQuizResult(result);
      setProgress(prev => ({
        is_completed: result.passed || prev?.is_completed,
        best_score: prev?.best_score === null ? result.score : Math.max(prev?.best_score ?? 0, result.score),
      }));
      setQuizMode('submitted');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error(err);
    }
  }
  // Keep ref in sync so the timer effect always calls the latest version
  submitQuizRef.current = handleSubmitQuiz;

  async function handleMarkComplete() {
    setMarking(true);
    try {
      await markModuleComplete(courseId, moduleId);
      setProgress({ is_completed: true, best_score: null });
    } catch (err) {
      console.error(err);
    } finally {
      setMarking(false);
    }
  }

  /* ── Quiz: taking mode ── */
  if (quizMode === 'taking') {
    const questions = quiz.questions;
    const q = questions[currentQ];
    const allAnswered = questions.every(q => answers[q.id] !== undefined);

    return (
      <div className="app">
        <StudentSidebar course={sidebarCourse} modules={sidebarModules} currentModuleId={String(moduleId)} />
        <div className="main anim-fade">
          <div className="ph">
            <div>
              <div className="bc">Student / {toTitleCase(course.title)} / Module {mod.sort_order} / Quiz</div>
              <div className="pt">{toTitleCase(mod.title)} — Quiz</div>
              <div className="ps">Question {currentQ + 1} of {questions.length}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* ── Countdown timer ── */}
              {timeLeft !== null && (() => {
                const m = Math.floor(timeLeft / 60);
                const s = timeLeft % 60;
                const display = `${m}:${s.toString().padStart(2, '0')}`;
                const isUrgent = timeLeft <= 60;       // < 1 min — red
                const isWarning = timeLeft <= 120;     // < 2 min — orange
                const color = isUrgent ? '#dc2626' : isWarning ? '#ea580c' : 'var(--ink)';
                const bg    = isUrgent ? '#fee2e2' : isWarning ? '#fff7ed' : 'var(--surface)';
                const border= isUrgent ? '#fca5a5' : isWarning ? '#fed7aa' : 'var(--border)';
                return (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px', borderRadius: 8,
                    background: bg, border: `1px solid ${border}`,
                    fontFamily: "'DM Mono', monospace", fontWeight: 700,
                    fontSize: 15, color,
                    animation: isUrgent ? 'pulse 1s ease-in-out infinite' : 'none',
                  }}>
                    ⏱ {display}
                  </div>
                );
              })()}
              <button
                className="btn btn-o btn-sm"
                onClick={() => { if (window.confirm('Leave quiz? Your progress will be lost.')) { setTimeLeft(null); setQuizMode(null); } }}
              >✕ Exit Quiz</button>
            </div>
          </div>

          <div className="body">
            <div style={{ maxWidth: 700 }}>
              {/* Progress bar */}
              <div style={{ marginBottom: 20 }}>
                <div className="pbar" style={{ height: 6 }}>
                  <div className="pfill" style={{ width: `${(currentQ + 1) / questions.length * 100}%`, transition: 'width .3s ease' }} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, textAlign: 'right' }}>{currentQ + 1} / {questions.length}</div>
              </div>

              {/* Question card */}
              <div className="card" key={currentQ} style={{ animation: 'slideUp .25s ease' }}>
                <div style={{ fontFamily: 'Nunito Sans,sans-serif', fontWeight: 700, fontSize: 15, marginBottom: 18 }}>
                  Q{currentQ + 1}. {q.text}
                </div>
                {q.options.map(opt => (
                  <div
                    key={opt.id}
                    className={`quiz-opt${answers[q.id] === opt.id ? ' sel' : ''}`}
                    onClick={() => setAnswers(prev => ({ ...prev, [q.id]: opt.id }))}
                  >
                    <div className="q-radio" />
                    <span>{opt.text}</span>
                  </div>
                ))}
              </div>

              {/* Navigation */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
                <button
                  className="btn btn-o btn-sm"
                  disabled={currentQ === 0}
                  style={{ opacity: currentQ === 0 ? 0.4 : 1 }}
                  onClick={() => setCurrentQ(q => q - 1)}
                >← Previous</button>
                {currentQ < questions.length - 1 ? (
                  <button
                    className="btn btn-p btn-sm"
                    disabled={!answers[q.id]}
                    style={{ opacity: !answers[q.id] ? 0.4 : 1 }}
                    onClick={() => setCurrentQ(q => q + 1)}
                  >Next →</button>
                ) : (
                  <button
                    className="btn btn-g btn-sm"
                    disabled={!allAnswered}
                    style={{ opacity: !allAnswered ? 0.4 : 1, cursor: !allAnswered ? 'not-allowed' : 'pointer' }}
                    onClick={handleSubmitQuiz}
                  >Submit Quiz ✓</button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Quiz: results mode ── */
  if (quizMode === 'submitted' && quizResult) {
    const { score, correct, total, passed, passmark, detail, show_answers: showAnswers, allow_retake: allowRetake } = quizResult;

    return (
      <div className="app">
        <StudentSidebar course={sidebarCourse} modules={sidebarModules} currentModuleId={String(moduleId)} />
        <div className="main anim-fade">
          <div className="ph">
            <div>
              <div className="bc">Student / {toTitleCase(course.title)} / Module {mod.sort_order} / Quiz Results</div>
              <div className="pt">Quiz Results</div>
            </div>
          </div>

          <div className="body">
            <div style={{ maxWidth: 700 }}>
              {/* Score card */}
              <div className="card" style={{ textAlign: 'center', padding: 36, marginBottom: 20, animation: 'popIn .4s ease' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>{passed ? '🎉' : '📝'}</div>
                <div style={{ fontFamily: 'Nunito Sans,sans-serif', fontWeight: 800, fontSize: 36, color: passed ? 'var(--green)' : 'var(--orange)', marginBottom: 4 }}>{score}%</div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                  {passed ? 'Congratulations, you passed!' : 'Not quite — keep learning!'}
                </div>
                <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18 }}>
                  Pass mark: {passmark}% · {quizResult.student_marks}/{quizResult.total_marks} correct
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  <button className="btn btn-o btn-sm" onClick={() => setQuizMode(null)}>← Back to Module</button>
                  {allowRetake && (
                    <button className={`btn ${passed ? 'btn-o' : 'btn-p'} btn-sm`} onClick={startQuiz}>↺ Retake Quiz</button>
                  )}
                </div>
              </div>

              {/* Answer review — only if instructor enabled it */}
              {showAnswers ? (
                <>
                  <div style={{ fontFamily: 'Nunito Sans,sans-serif', fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Review Answers</div>
                  {quiz.questions.map((q, qi) => {
                    const d = detail.find(d => d.question_id === q.id);
                    return (
                      <div key={q.id} className="card" style={{ marginBottom: 12, animation: 'slideUp .25s ease both', animationDelay: `${qi * 0.06}s` }}>
                        <div style={{ fontFamily: 'Nunito Sans,sans-serif', fontWeight: 700, fontSize: 13.5, marginBottom: 12 }}>
                          Q{qi + 1}. {q.text}
                        </div>
                        {q.options.map(opt => {
                          const isCorrect = d?.correct_option_id === opt.id;
                          const isChosen  = d?.chosen_option_id  === opt.id;
                          const cls = isCorrect ? ' correct' : (isChosen && !isCorrect ? ' wrong' : '');
                          return (
                            <div key={opt.id} className={`quiz-opt${cls}`} style={{ cursor: 'default' }}>
                              <div className="q-radio" />
                              <span>{opt.text}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </>
              ) : (
                <div className="card" style={{ textAlign: 'center', padding: 24, color: 'var(--muted)', fontSize: 13 }}>
                  Answer review is not available for this quiz.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Normal module view ── */
  // Support both new module_videos table AND legacy single video_file_path
  const rawVideos = mod.videos || [];
  const modVideos = rawVideos.length > 0
    ? rawVideos
    : (mod.video_file_path
        ? [{ id: 'legacy', name: mod.video_file_name || 'video.mp4', file_path: mod.video_file_path, size: null }]
        : []);
  const activeVideo = modVideos.find(v => v.id === selectedVideoId) || modVideos[0] || null;
  const hasAnyContent = modVideos.length > 0 || mod.presentation_available || resources.length > 0 || quiz.questions.length > 0;

  // Build tabs — only show tabs that have content
  const TABS = [
    { key: 'overview',  icon: '◎', label: 'Overview',  badge: null },
    { key: 'video',     icon: '▶', label: 'Video',     badge: modVideos.length > 0 ? modVideos.length : null },
    { key: 'slides',    icon: '⊟', label: 'Slides',    badge: null },
    { key: 'resources', icon: '⊕', label: 'Resources', badge: resources.length || null },
    { key: 'quiz',      icon: '✎', label: 'Quiz',      badge: quiz.questions.length || null },
  ];

  const visibleTab = TABS.find(t => t.key === activeTab) ? activeTab : 'video';

  return (
    <div className="app">
      <StudentSidebar course={sidebarCourse} modules={sidebarModules} currentModuleId={String(moduleId)} />
      <div className="main anim-fade">
        <div className="ph">
          <div>
            <div className="bc">Student / {toTitleCase(course.title)} / Module {mod.sort_order}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn btn-o btn-sm" onClick={() => navigate(`/student/courses/${courseId}`)}>← Course</button>
            {isCompleted && <span className="b b-g" style={{ padding: '6px 14px' }}>✓ Completed</span>}
          </div>
        </div>

        <div className="body">
          <div style={{ maxWidth: 860 }}>

            {/* ── Tab Bar ── */}
            <div className="tab-bar" style={{ marginBottom: 20 }}>
              {TABS.map(t => (
                <button
                  key={t.key}
                  className={`tab${visibleTab === t.key ? ' on' : ''}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.icon} {t.label}
                  {t.badge != null && (
                    <span className="b b-y" style={{ marginLeft: 6, fontSize: 11, padding: '1px 7px' }}>{t.badge}</span>
                  )}
                </button>
              ))}
            </div>

            {/* ── Overview Tab ── */}
            {visibleTab === 'overview' && (
              <div className="card">
                <div style={{ fontFamily: "'Nunito Sans',sans-serif", fontWeight: 700, fontSize: 15, marginBottom: 14 }}>
                  {toTitleCase(mod.title)}
                </div>
                {mod.overview ? (
                  <div className="rich-text" style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.85 }}
                    dangerouslySetInnerHTML={{ __html: mod.overview }} />
                ) : (
                  <p style={{ fontSize: 13, color: 'var(--muted)' }}>No overview added for this module yet.</p>
                )}
              </div>
            )}

            {/* ── Video Tab ── */}
            {visibleTab === 'video' && (
              <div className="card">
                {modVideos.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 44, color: 'var(--muted)' }}>
                    <div style={{ fontSize: 44, marginBottom: 12 }}>🎬</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>Video not yet available</div>
                  </div>
                ) : (
                  <>
                    {/* Video list — click to select */}
                    {modVideos.length > 1 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                          Select a video to watch
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {modVideos.map((v, vi) => {
                            const isActive = activeVideo && activeVideo.id === v.id;
                            return (
                              <div
                                key={v.id}
                                onClick={() => setSelectedVideoId(v.id)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 12,
                                  padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                                  background: isActive ? 'var(--yellow-soft)' : 'var(--surface)',
                                  border: `1.5px solid ${isActive ? 'var(--yellow)' : 'var(--border)'}`,
                                  transition: 'all .15s',
                                }}
                              >
                                <div style={{
                                  width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  background: isActive ? 'var(--yellow)' : 'var(--border)',
                                  fontSize: 15,
                                }}>
                                  {isActive ? '▶' : `${vi + 1}`}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: isActive ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</div>
                                  {v.size && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{v.size}</div>}
                                </div>
                                {isActive && <span className="b b-g" style={{ fontSize: 11 }}>▶ Playing</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Player */}
                    {activeVideo && (
                      <>
                        {modVideos.length === 1 && (
                          <div className="ct">
                            <span>▶ {activeVideo.name}</span>
                            {mod.duration_sec && <span className="b b-y">{fmtDuration(mod.duration_sec)}</span>}
                          </div>
                        )}
                        {modVideos.length > 1 && (
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                            Now Playing — {activeVideo.name}
                          </div>
                        )}
                        <video
                          controls
                          key={activeVideo.file_path}
                          style={{ width: '100%', borderRadius: 10, background: '#000', display: 'block', maxHeight: 440 }}
                        >
                          <source src={`/api/instructor/uploads/${activeVideo.file_path}`} />
                          Your browser does not support video playback.
                        </video>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── Slides Tab ── */}
            {visibleTab === 'slides' && (
              <div className="card">
                <div className="ct"><span>📑 Presentation Slides</span></div>
                {!mod.presentation_available ? (
                  <div style={{ textAlign: 'center', padding: 44, color: 'var(--muted)' }}>
                    <div style={{ fontSize: 44, marginBottom: 12 }}>📑</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>No slides uploaded yet</div>
                  </div>
                ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: 'var(--surface)', borderRadius: 'var(--rs)', border: '1px solid var(--border)' }}>
                  <div style={{ width: 44, height: 44, background: 'var(--yellow-soft)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>📑</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{mod.presentation_file_name || 'Course Slides'}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Click download to open or save</div>
                  </div>
                  {mod.presentation_file_path && (
                    <button className="btn btn-p btn-sm" onClick={() => downloadFile(mod.presentation_file_path, mod.presentation_file_name)}>↓ Download</button>
                  )}
                </div>
                )}
              </div>
            )}

            {/* ── Resources Tab ── */}
            {visibleTab === 'resources' && (
              <div className="card">
                <div className="ct">
                  <span>📎 Resources</span>
                  {resources.length > 0 && <span className="b b-y">{resources.length} file{resources.length !== 1 ? 's' : ''}</span>}
                </div>
                {resources.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 44, color: 'var(--muted)' }}>
                    <div style={{ fontSize: 44, marginBottom: 12 }}>📎</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>No resources uploaded yet</div>
                  </div>
                ) : resources.map(r => {
                  const isLink = r.type === 'link';
                  const typeMap = { pdf: ['📄', 'res-icon-pdf'], xls: ['📊', 'res-icon-xls'], xlsx: ['📊', 'res-icon-xls'], link: ['🔗', 'res-icon-link'], doc: ['📝', 'res-icon-doc'], docx: ['📝', 'res-icon-doc'] };
                  const [icon, iconCls] = typeMap[r.type] || ['📎', 'res-icon-doc'];
                  return (
                    <div key={r.id} className="res-item">
                      <div className={`res-icon ${iconCls}`}>{icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{r.name}</div>
                        {r.size && <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{r.size}</div>}
                      </div>
                      {isLink && r.link_url
                        ? <a href={r.link_url} target="_blank" rel="noreferrer"
                            style={{ fontSize: 12.5, color: '#1A56DB', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: '#E8F0FE', borderRadius: 6, textDecoration: 'none' }}>
                            ↗ Open Link
                          </a>
                        : r.file_path
                          ? <button className="btn btn-o btn-sm" onClick={() => downloadFile(r.file_path, r.name)}>↓ Download</button>
                          : <button className="btn btn-o btn-sm" disabled>↓ Download</button>
                      }
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Quiz Tab ── */}
            {visibleTab === 'quiz' && (
              quiz.questions.length > 0 ? (
                <div className="card">
                  <div className="ct">
                    <span>📝 Module Quiz</span>
                    {hasPassed ? <span className="b b-g">Passed</span> : <span className="b b-o">Required to complete module</span>}
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
                    {quiz.questions.length} question{quiz.questions.length !== 1 ? 's' : ''} · Pass mark: {quiz.passmark}%
                    {quiz.time_limit ? ` · ⏱ ${quiz.time_limit} min time limit` : ' · No time limit'}
                  </p>
                  {attempts.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Previous Attempts</div>
                      {attempts.map(att => (
                        <div key={att.attempt_num} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--surface)', borderRadius: 'var(--rs)', border: '1px solid var(--border)', marginBottom: 4 }}>
                          <span style={{ fontSize: 12 }}>
                            Attempt {att.attempt_num} · {att.date}
                            {att.total_marks != null && <span style={{ color: 'var(--muted)', marginLeft: 6 }}>({att.student_marks}/{att.total_marks} correct)</span>}
                          </span>
                          <span className={`b ${att.score >= quiz.passmark ? 'b-g' : 'b-r'}`}>{att.score}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(!hasPassed || quiz.allow_retake) && (
                    <button className={`btn ${hasPassed ? 'btn-o' : 'btn-p'} btn-sm`} onClick={startQuiz}>
                      {hasPassed ? '↺ Retake Quiz' : 'Start Quiz →'}
                    </button>
                  )}
                </div>
              ) : (
                <div className="card" style={{ textAlign: 'center', padding: 36 }}>
                  {!isCompleted ? (
                    <>
                      <div style={{ fontSize: 44, marginBottom: 12 }}>✅</div>
                      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>No quiz for this module</div>
                      {hasAnyContent ? (
                        <>
                          <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18 }}>Mark it complete when you're done reviewing the content.</p>
                          <button className="btn btn-p" disabled={marking} onClick={handleMarkComplete}>
                            {marking ? 'Saving…' : '✓ Mark as Complete'}
                          </button>
                        </>
                      ) : (
                        <p style={{ fontSize: 13, color: 'var(--muted)' }}>Content will be added soon.</p>
                      )}
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 44, marginBottom: 12 }}>🎉</div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>Module Complete!</div>
                    </>
                  )}
                </div>
              )
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
